// Percent renderer — plan §6.3. Kategori listesi + toplam 100% kontrolü.
// UX P1 K5 — boş alan 0 DEĞİL: kategori anahtarı silinir; hiç kategori kalmazsa değer
// `null` (yanıt temizlenir — sunucu null'u her tipte boş kabul eder, zorunluysa eksik
// listesine düşer). "Temizle" düğmesi tüm dağılımı tek tıkla siler. Eskiden boş input
// `Number("") === 0` yazıyordu; toplam 0 → 422 → yanıt hiç temizlenemiyordu.

import type { QuestionSnapshot } from "@mahir/shared";
import { FORM_SAVE_COPY as C } from "../../../lib/form-save-copy";

interface Props {
  question: QuestionSnapshot;
  value: Record<string, number> | null;
  onChange: (v: Record<string, number> | null) => void;
}

/**
 * Saf — bir kategori input'unun ham metninden sonraki değeri üretir. Boş/geçersiz/negatif
 * metin anahtarı siler; sonuç boş nesneyse `null` döner (yanıt temizlenir).
 */
export function nextPercentValue(
  current: Record<string, number> | null,
  categoryId: string,
  raw: string,
): Record<string, number> | null {
  const next = { ...(current ?? {}) };
  const trimmed = raw.trim();
  const n = trimmed === "" ? Number.NaN : Number(trimmed);
  if (!Number.isFinite(n) || n < 0) delete next[categoryId];
  else next[categoryId] = n;
  return Object.keys(next).length === 0 ? null : next;
}

interface CategoryItem {
  id: string;
  label: string;
}

export function PercentRenderer({ question, value, onChange }: Props): JSX.Element {
  const categories = (question.options as { categories?: CategoryItem[] } | null)?.categories ?? [];
  const v = value ?? {};
  const total = Object.values(v).reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
  const isValid = Math.abs(total - 100) < 0.1;
  const isEmpty = Object.keys(v).length === 0;

  const setCat = (id: string, raw: string): void => {
    onChange(nextPercentValue(value, id, raw));
  };

  return (
    <div>
      {categories.length === 0 ? (
        <div
          className="text-xs px-2 py-1 rounded"
          style={{ background: "var(--color-bg)", color: "var(--color-text-3)" }}
        >
          Bu soru için kategori tanımlanmamış.
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-3">
                <label style={{ flex: 1, color: "var(--color-text)", fontSize: "0.88rem" }}>
                  {c.label}
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={v[c.id] ?? ""}
                  onChange={(e) => setCat(c.id, e.target.value)}
                  className="rounded-lg px-3 py-1.5 text-sm"
                  style={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-text)",
                    fontFamily: "var(--font-mono)",
                    width: 100,
                  }}
                />
                <span
                  style={{
                    color: "var(--color-text-3)",
                    fontFamily: "var(--font-mono)",
                    fontSize: "0.85rem",
                  }}
                >
                  %
                </span>
              </div>
            ))}
          </div>
          <div
            className="mt-3 px-3 py-2 rounded-lg text-sm flex justify-between"
            style={{
              background: isEmpty
                ? "var(--color-bg)"
                : isValid
                  ? "color-mix(in srgb, var(--color-success) 10%, white)"
                  : "color-mix(in srgb, var(--color-error) 10%, white)",
              color: isEmpty
                ? "var(--color-text-3)"
                : isValid
                  ? "var(--color-success)"
                  : "var(--color-error)",
              fontFamily: "var(--font-mono)",
            }}
          >
            <span>Toplam</span>
            <span>
              {total.toFixed(1)}%{" "}
              {isEmpty ? "(boş)" : isValid ? "✓" : `(100 olmalı, fark ${(100 - total).toFixed(1)})`}
            </span>
          </div>
          {!isEmpty && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => onChange(null)}
                aria-label={C.percentClearAria}
                className="px-2 py-1 rounded text-xs"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-2)",
                }}
              >
                {C.percentClear}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
